import { Fragment, useState } from "react";
import { Chip, Pagination } from "@heroui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { parseNum } from "../domain/status";
import { UNRESOLVED_STATUSES } from "../domain/constants";
import type { ColDef, ColGroup, FlatRow, Row } from "../domain/types";
import type { ReconciliationCompanyRow } from "../../services/api";
import { ScrollableX } from "../../components/ScrollableX";

const TABLE_MAX_HEIGHT = "calc(100vh - 340px)";

// Solid colors, white text. Blue family = Issuer, amber family = Position.
// White thick border creates clear sub-section gaps within each family.
const GROUP_META: Record<ColGroup | "other", { label: string; thCls: string }> = {
  identity:       { label: "Identity",       thCls: "bg-blue-500   text-white border-r-4 border-white" },
  classification: { label: "Classification", thCls: "bg-blue-600   text-white border-r-4 border-white" },
  tracking:       { label: "Tracking",       thCls: "bg-blue-700   text-white border-r-4 border-white" },
  context:        { label: "Context",        thCls: "bg-amber-400  text-white border-r-4 border-white" },
  financials:     { label: "Financials",     thCls: "bg-amber-500  text-white border-r-4 border-white" },
  status:         { label: "Status",         thCls: "bg-amber-600  text-white border-r-4 border-white" },
  description:    { label: "Description",    thCls: "bg-slate-400  text-white border-r   border-slate-300" },
  other:          { label: "",               thCls: "bg-slate-300  text-slate-600 border-r border-slate-200" },
};


function sumPositionCol(rows: FlatRow[], col: ColDef): string {
  if (col.align !== "right") return "—";
  let total = 0;
  let hasAny = false;
  for (const row of rows) {
    const v = parseNum(row.positionInfo[col.key]);
    if (v != null) {
      total += v;
      hasAny = true;
    }
  }
  if (!hasAny) return "—";
  return col.fmt(total);
}

function getRealizedLabel(positionInfo: Row): string | null {
  const state = String(positionInfo.company_investment_state ?? "").toLowerCase().trim();
  if (!state || state.startsWith("unrealized")) return null;
  if (state.startsWith("partially")) return "Partially realized";
  if (state.startsWith("realized")) return "Realized";
  return null;
}

export function CompanyReconciliationSection({
  activeFundId,
  selectedFundsById,
  companies,
  companiesTotal,
  totalPositionsCount,
  currentPage,
  pageCount,
  onPageChange,
  issuerMap,
  controlCompaniesByFund,
  controlsByFundIssuer,
  companyIssuerColsNoShortDescription,
  companyPositionCols,
  companyShortDescriptionCol,
  renderCell,
  getIssueTypeLabel,
  getPriority,
  fmtNum,
  selectedPeriodLabel,
}: {
  activeFundId: number;
  selectedFundsById: Map<number, { fund_name?: string }>;
  companies: ReconciliationCompanyRow[];
  companiesTotal: number;
  totalPositionsCount: number;
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  issuerMap: Map<number, Row>;
  controlCompaniesByFund: Map<number, string[]>;
  controlsByFundIssuer: Map<string, any[]>;
  companyIssuerColsNoShortDescription: ColDef[];
  companyPositionCols: ColDef[];
  companyShortDescriptionCol?: ColDef;
  renderCell: (args: {
    entityType: "issuer" | "position";
    entityId: number;
    fundId: number;
    issuerId: number;
    reportingDate: number | null;
    row: Row;
    col: ColDef;
    fundName: string;
    companyName: string;
    previousRow?: Row;
    disablePreviousComparison?: boolean;
    hideDot?: boolean;
  }) => React.ReactNode;
  getIssueTypeLabel: (reasonCode?: string, status?: string) => string;
  getPriority: (status?: string, materialityAbs?: number | null) => string;
  fmtNum: (v: unknown) => string;
  selectedPeriodLabel: string;
}) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const fund = selectedFundsById.get(activeFundId);
  const fundName = fund?.fund_name || `Fund ${activeFundId}`;
  const controlCompanies = controlCompaniesByFund.get(activeFundId) ?? [];

  return (
    <div className="mt-10">
      <div className="mb-5 flex items-center gap-3">
        <h2 className="text-2xl font-serif text-slate-900">
          Portfolio Positions
        </h2>
        {companies.length === 0 && controlCompanies.length === 0 ? (
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-200">—</span>
        ) : (
          <>
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
              page {currentPage} / {pageCount}
            </span>
            <span className="text-xs text-slate-400">
              {companiesTotal} issuer{companiesTotal !== 1 ? "s" : ""}
            </span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs text-slate-400">
              {totalPositionsCount} position{totalPositionsCount !== 1 ? "s" : ""}
            </span>
          </>
        )}
      </div>

      {companies.length === 0 && controlCompanies.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
          No positions for this fund on {selectedPeriodLabel}.
        </div>
      ) : companies.length === 0 ? (
        <ScrollableX className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-600">
              <tr>
                <th className="sticky left-0 z-20 bg-slate-50/95 px-3 py-2.5 text-left">Company</th>
                <th className="px-3 py-2.5 text-left">Issues</th>
                <th className="px-3 py-2.5 text-left">Max priority</th>
                <th className="px-3 py-2.5 text-left">Issue type</th>
                <th className="px-3 py-2.5 text-left">Impact</th>
              </tr>
            </thead>
            <tbody>
              {controlCompanies.map((issuerName) => {
                const key = `${fundName.toLowerCase()}::${issuerName}`;
                const entries = controlsByFundIssuer.get(key) ?? [];
                const open = entries.filter((x) =>
                  (UNRESOLVED_STATUSES as readonly string[]).includes(x.status),
                );
                const impact = open.reduce((acc, x) => acc + (x.materialityAbs ?? 0), 0);
                return (
                  <tr key={issuerName} className="border-t border-slate-100">
                    <td className="px-3 py-2.5 font-medium text-slate-800">{issuerName}</td>
                    <td className="px-3 py-2.5">{open.length}</td>
                    <td className="px-3 py-2.5">
                      {open.some((x) => getPriority(x.status, x.materialityAbs) === "P0")
                        ? "P0"
                        : open.length > 0 ? "P1" : "OK"}
                    </td>
                    <td className="px-3 py-2.5">
                      {open.length > 0 ? getIssueTypeLabel(open[0].reasonCode, open[0].status) : "-"}
                    </td>
                    <td className="px-3 py-2.5">{fmtNum(impact)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollableX>
      ) : (
        <>
          <ScrollableX className="rounded-xl border border-slate-200 bg-white shadow-sm" maxHeight={TABLE_MAX_HEIGHT}>
            <table className="w-full text-sm">
              <thead>
                {/* ── Group row (h-10 = 40px fixed, so column headers use top-10) ── */}
                <tr>
                  <th className="sticky left-0 top-0 z-40 h-10 bg-slate-500 border-r-4 border-white" />
                  {[...companyIssuerColsNoShortDescription, ...companyPositionCols].reduce<{ spans: { group: ColGroup | "other"; count: number }[] }>((acc, col) => {
                    const g = (col.group ?? "other") as ColGroup | "other";
                    if (acc.spans.length > 0 && acc.spans[acc.spans.length - 1].group === g) {
                      acc.spans[acc.spans.length - 1].count++;
                    } else {
                      acc.spans.push({ group: g, count: 1 });
                    }
                    return acc;
                  }, { spans: [] }).spans.map((s, i) => {
                    const meta = GROUP_META[s.group];
                    return (
                      <th key={`g-${i}`} colSpan={s.count} className={`sticky top-0 z-20 h-10 text-center text-[11px] font-semibold uppercase tracking-wide ${meta.thCls}`}>
                        {meta.label}
                      </th>
                    );
                  })}
                  {companyShortDescriptionCol && (
                    <th className={`sticky top-0 z-20 h-10 text-center text-[11px] font-semibold uppercase tracking-wide ${GROUP_META.description.thCls}`}>
                      {GROUP_META.description.label}
                    </th>
                  )}
                </tr>
                {/* ── Column header row (top-10 = 40px, flush with group row) ── */}
                <tr className="bg-slate-50/80">
                  <th className="sticky left-0 top-10 z-40 bg-slate-50/95 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 border-b border-slate-200 whitespace-nowrap">Company</th>
                  {companyIssuerColsNoShortDescription.map((col) => (
                    <th key={`issuer-h-${col.key}`} className="sticky top-10 z-10 bg-slate-50/95 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 border-b border-slate-200 whitespace-nowrap">{col.header}</th>
                  ))}
                  {companyPositionCols.map((col) => (
                    <th key={`position-h-${col.key}`} className="sticky top-10 z-10 bg-slate-50/95 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 border-b border-slate-200 whitespace-nowrap">{col.header}</th>
                  ))}
                  {companyShortDescriptionCol && (
                    <th className="sticky top-10 z-10 bg-slate-50/95 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 border-b border-slate-200 whitespace-nowrap">{companyShortDescriptionCol.header}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => {
                  const issuerId = company.issuer_id;
                  const issuerInfo = issuerMap.get(issuerId) ?? ({} as Row);
                  const isRemoved = company.status === "removed";
                  const isAdded = company.status === "added";

                  // Build flat rows from company positions (guard against null from backend)
                  const positions = ((isRemoved ? company.previous_positions : company.positions) ?? []) as Row[];
                  const previousPositions = isRemoved ? [] : ((company.previous_positions ?? []) as Row[]);

                  const flatRows: FlatRow[] = positions.map((pos) => ({
                    fundId: activeFundId,
                    issuerId,
                    positionId: Number((pos as Record<string, unknown>).id ?? 0),
                    companyName: company.company_name,
                    issuerInfo,
                    positionInfo: pos,
                  }));

                  const isMulti = flatRows.length > 1;
                  const firstRow = flatRows[0];
                  const reportingDate = firstRow
                    ? parseNum((firstRow.positionInfo as Record<string, unknown>).reporting_date)
                    : null;
                  const groupKey = `${issuerId}:${reportingDate ?? 0}`;
                  const isExpanded = expandedGroups.has(groupKey);
                  const previousRow = previousPositions[0] as Row | undefined;
                  const realizedLabel = firstRow
                    ? getRealizedLabel(firstRow.positionInfo as Row)
                    : null;

                  const trStyle = isRemoved
                    ? "border-t border-rose-200 bg-rose-50/70"
                    : "border-t border-slate-100";

                  const firstCellStyle = isRemoved
                    ? "sticky left-0 z-10 bg-rose-50/90 px-3 py-2.5 font-medium text-rose-800"
                    : "sticky left-0 z-10 bg-white px-3 py-2.5 font-medium text-slate-800";

                  return (
                    <Fragment key={`company-${issuerId}`}>
                      <tr className={trStyle}>
                        <td className={firstCellStyle}>
                          <div className="flex items-center gap-2">
                            {isMulti && !isRemoved && (
                              <button
                                type="button"
                                onClick={() => toggleGroup(groupKey)}
                                className="shrink-0 text-slate-400 hover:text-slate-700"
                              >
                                {isExpanded
                                  ? <ChevronDownIcon className="h-3.5 w-3.5" />
                                  : <ChevronRightIcon className="h-3.5 w-3.5" />}
                              </button>
                            )}
                            <span>{company.company_name}</span>
                            <span className={`text-[10px] font-mono ${isRemoved ? "text-rose-400" : "text-slate-400"}`}>
                              #{issuerId}
                            </span>
                            {isRemoved && (
                              <Chip size="sm" color="danger" variant="flat">- Removed</Chip>
                            )}
                            {isAdded && (
                              <Chip size="sm" color="success" variant="flat">Added</Chip>
                            )}
                            {realizedLabel && (
                              <Chip size="sm" color="danger" variant="solid" className="text-[10px]">
                                {realizedLabel}
                              </Chip>
                            )}
                            {isMulti && !isRemoved && (
                              <Chip size="sm" color="default" variant="flat" className="text-[10px]">
                                {flatRows.length} positions
                              </Chip>
                            )}
                          </div>
                        </td>
                        {companyIssuerColsNoShortDescription.map((col) => (
                          <td
                            key={`issuer-${issuerId}-${col.key}`}
                            className={`px-3 py-2.5 transition-colors ${isRemoved ? "hover:bg-rose-100/70" : "hover:bg-slate-100/70"}`}
                          >
                            {renderCell({
                              entityType: "issuer",
                              entityId: issuerId,
                              fundId: activeFundId,
                              issuerId,
                              reportingDate,
                              row: issuerInfo,
                              col,
                              fundName,
                              companyName: company.company_name,
                              hideDot: isRemoved,
                              previousRow,
                            })}
                          </td>
                        ))}
                        {companyPositionCols.map((col) => (
                          <td
                            key={`position-${issuerId}-${col.key}`}
                            className={
                              col.align === "right"
                                ? `px-3 py-2.5 text-right transition-colors ${isRemoved ? "text-rose-800 bg-rose-50/70" : "hover:bg-slate-100/70"}`
                                : `px-3 py-2.5 transition-colors ${isRemoved ? "text-rose-800 bg-rose-50/70" : "hover:bg-slate-100/70"}`
                            }
                          >
                            {isRemoved ? (
                              col.fmt(firstRow?.positionInfo?.[col.key])
                            ) : isMulti ? (
                              <span className="text-slate-400 text-xs">
                                {sumPositionCol(flatRows, col)}
                              </span>
                            ) : firstRow ? (
                              renderCell({
                                entityType: "position",
                                entityId: firstRow.positionId,
                                fundId: activeFundId,
                                issuerId,
                                reportingDate,
                                row: firstRow.positionInfo as Row,
                                col,
                                fundName,
                                companyName: company.company_name,
                                previousRow,
                              })
                            ) : null}
                          </td>
                        ))}
                        {companyShortDescriptionCol && (
                          <td className={`max-w-[660px] w-[660px] px-3 py-2.5 transition-colors ${isRemoved ? "hover:bg-rose-100/70" : "hover:bg-slate-100/70"}`}>
                            {renderCell({
                              entityType: "issuer",
                              entityId: issuerId,
                              fundId: activeFundId,
                              issuerId,
                              reportingDate,
                              row: issuerInfo,
                              col: companyShortDescriptionCol,
                              fundName,
                              companyName: company.company_name,
                              hideDot: isRemoved,
                            })}
                          </td>
                        )}
                      </tr>
                      {/* Child rows for multi-position groups */}
                      {isMulti && !isRemoved && isExpanded &&
                        flatRows.map((row, idx) => (
                          <tr
                            key={`child-${issuerId}-${row.positionId}`}
                            className="border-t border-slate-50 bg-slate-50/50"
                          >
                            <td className="sticky left-0 z-10 bg-slate-50/80 px-3 py-1.5 text-slate-500">
                              <span className="pl-6 text-xs">↳ #{idx + 1}</span>
                            </td>
                            {companyIssuerColsNoShortDescription.map((col) => (
                              <td key={`child-issuer-${issuerId}-${col.key}-${idx}`} className="px-3 py-1.5" />
                            ))}
                            {companyPositionCols.map((col) => (
                              <td
                                key={`child-pos-${row.positionId}-${col.key}`}
                                className={
                                  col.align === "right"
                                    ? "px-3 py-1.5 text-right transition-colors hover:bg-slate-100/70"
                                    : "px-3 py-1.5 transition-colors hover:bg-slate-100/70"
                                }
                              >
                                {renderCell({
                                  entityType: "position",
                                  entityId: row.positionId,
                                  fundId: activeFundId,
                                  issuerId,
                                  reportingDate,
                                  row: row.positionInfo as Row,
                                  col,
                                  fundName,
                                  companyName: company.company_name,
                                  previousRow,
                                  disablePreviousComparison: idx > 0,
                                })}
                              </td>
                            ))}
                            {companyShortDescriptionCol && <td className="px-3 py-1.5" />}
                          </tr>
                        ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </ScrollableX>
          {pageCount > 1 && (
            <div className="mt-3 flex justify-center">
              <Pagination
                showControls
                total={pageCount}
                page={currentPage}
                onChange={onPageChange}
                size="sm"
                radius="md"
                color="primary"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
